import { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Checkbox,
  Link,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuthStore } from "../../stores/root_store";
import { taskPaymentApi } from "../../api/task_payment_api";
import type {
  PaidTask,
  PaidTasksResult,
  MarkUnpaidResult,
} from "../../api/task_payment_api";

const DEFAULT_RANGE_DAYS = 7;

function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

function getDefaultStartDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_RANGE_DAYS);
  return formatDateForInput(date);
}

function getDefaultEndDate(): string {
  return formatDateForInput(new Date());
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString();
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString();
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

interface SolverGroup {
  key: string;
  name: string;
  email: string | null;
  tasks: PaidTask[];
  total: number;
}

function groupBySolver(tasks: PaidTask[]): SolverGroup[] {
  const groups = new Map<string, SolverGroup>();
  for (const task of tasks) {
    const key = task.solved_by ?? "unknown";
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        name: task.solver_name || task.solver_email || "Unknown solver",
        email: task.solver_email,
        tasks: [],
        total: 0,
      };
      groups.set(key, group);
    }
    group.tasks.push(task);
    group.total += task.solver_paid_amount;
  }
  return Array.from(groups.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export const PaidTasksPage = observer(() => {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateStart, setDateStart] = useState(
    searchParams.get("start") || getDefaultStartDate(),
  );
  const [dateEnd, setDateEnd] = useState(
    searchParams.get("end") || getDefaultEndDate(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PaidTasksResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [preview, setPreview] = useState<MarkUnpaidResult | null>(null);
  const [markLoading, setMarkLoading] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  const [markConfirmed, setMarkConfirmed] = useState(false);
  const [lastMarked, setLastMarked] = useState<MarkUnpaidResult | null>(null);

  const hasSudo = authStore.roles.includes("sudo");

  const loadTasks = async (start: string, end: string) => {
    try {
      setLoading(true);
      setError(null);
      setSelected(new Set());
      const data = await taskPaymentApi.listPaidTasks(start, end);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load paid tasks");
    } finally {
      setLoading(false);
    }
  };

  // A range in the URL loads on arrival so a link to this page is complete
  useEffect(() => {
    const start = searchParams.get("start");
    const end = searchParams.get("end");
    if (hasSudo && start && end) {
      loadTasks(start, end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSudo, searchParams]);

  const handleLoad = () => {
    setLastMarked(null);
    if (
      searchParams.get("start") === dateStart &&
      searchParams.get("end") === dateEnd
    ) {
      loadTasks(dateStart, dateEnd);
    } else {
      setSearchParams({ start: dateStart, end: dateEnd });
    }
  };

  const groups = useMemo(
    () => (result ? groupBySolver(result.tasks) : []),
    [result],
  );
  const tasksByKey = useMemo(() => {
    const map = new Map<string, PaidTask>();
    for (const task of result?.tasks ?? []) {
      map.set(task.request_key, task);
    }
    return map;
  }, [result]);

  const selectedTotal = useMemo(() => {
    let total = 0;
    for (const key of selected) {
      total += tasksByKey.get(key)?.solver_paid_amount ?? 0;
    }
    return total;
  }, [selected, tasksByKey]);

  const toggleTask = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const setKeys = (keys: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const key of keys) {
        if (on) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const allKeys = result ? result.tasks.map((t) => t.request_key) : [];
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
  const someSelected = allKeys.some((k) => selected.has(k));

  const handlePreviewMark = async () => {
    if (selected.size === 0) return;
    try {
      setMarkLoading(true);
      setMarkError(null);
      setMarkConfirmed(false);
      const data = await taskPaymentApi.markUnpaid(Array.from(selected), true);
      setPreview(data);
    } catch (err) {
      setMarkError(
        err instanceof Error ? err.message : "Failed to preview mark unpaid",
      );
    } finally {
      setMarkLoading(false);
    }
  };

  const closePreview = () => {
    setPreview(null);
    setMarkError(null);
    setMarkConfirmed(false);
  };

  const handleExecuteMark = async () => {
    try {
      setMarkLoading(true);
      setMarkError(null);
      const data = await taskPaymentApi.markUnpaid(Array.from(selected), false);
      setLastMarked(data);
      setPreview(null);
      setMarkConfirmed(false);
      await loadTasks(dateStart, dateEnd);
    } catch (err) {
      setMarkError(
        err instanceof Error ? err.message : "Failed to mark tasks unpaid",
      );
    } finally {
      setMarkLoading(false);
    }
  };

  if (!authStore.initialized) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!hasSudo) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You must have sudo access to view this page.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/assistant")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/manager/task-payment")}
        >
          Back to Task Payment
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        Paid Tasks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tasks marked paid between two dates, by the date they were marked paid.
        A task with no payment record was left paid after its batch was voided
        (or was marked paid by hand). Select tasks and mark them unpaid so the
        next payment run pays them.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Paid Between
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="Start Date"
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleLoad}
              disabled={loading || !dateStart || !dateEnd}
            >
              {loading ? <CircularProgress size={20} /> : "Load"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lastMarked && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setLastMarked(null)}
        >
          Marked {lastMarked.request_count} task(s) totaling{" "}
          {formatCurrency(lastMarked.reverted_amount)} unpaid for{" "}
          {lastMarked.user_count} assistant(s). They will be included in the
          next payment run.
        </Alert>
      )}

      {markError && !preview && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {markError}
        </Alert>
      )}

      {result && (
        <>
          {result.truncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              More tasks matched than can be shown. Narrow the date range to
              see all of them.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(e) => setKeys(allKeys, e.target.checked)}
                  disabled={allKeys.length === 0}
                />
              }
              label="Select all"
            />
            <Typography variant="body2" color="text.secondary">
              {result.task_count} task(s) · {result.user_count} assistant(s) ·{" "}
              {formatCurrency(result.total_amount)} marked paid{" "}
              {formatDate(`${result.date_start}T00:00:00Z`)} to{" "}
              {formatDate(`${result.date_end}T00:00:00Z`)}
            </Typography>
          </Box>

          {groups.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No tasks were marked paid in this range.
            </Alert>
          )}

          {groups.map((group) => {
            const groupKeys = group.tasks.map((t) => t.request_key);
            const groupAll = groupKeys.every((k) => selected.has(k));
            const groupSome = groupKeys.some((k) => selected.has(k));
            return (
              <Card key={group.key} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Checkbox
                      checked={groupAll}
                      indeterminate={!groupAll && groupSome}
                      onChange={(e) => setKeys(groupKeys, e.target.checked)}
                    />
                    <Typography variant="h6">{group.name}</Typography>
                    {group.email && (
                      <Typography variant="body2" color="text.secondary">
                        {group.email}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: "auto" }}
                    >
                      {group.tasks.length} task(s) · {formatCurrency(group.total)}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Request</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Date Solved</TableCell>
                          <TableCell>Marked Paid</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Payment Record</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.tasks.map((task) => {
                          const requestId = task.request_uuid || task.request_key;
                          const isSelected = selected.has(task.request_key);
                          return (
                            <TableRow
                              key={task.request_key}
                              hover
                              selected={isSelected}
                              onClick={() => toggleTask(task.request_key)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => toggleTask(task.request_key)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell>
                                <Link
                                  href={`/assistant/request/${requestId}`}
                                  target="_blank"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {task.request_uuid ||
                                    `${task.request_key.slice(0, 12)}…`}
                                </Link>
                              </TableCell>
                              <TableCell
                                sx={{
                                  maxWidth: 260,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {task.title || "-"}
                              </TableCell>
                              <TableCell>{formatDate(task.date_solved)}</TableCell>
                              <TableCell>
                                {formatDateTime(task.solver_paid_date)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(task.solver_paid_amount)}
                              </TableCell>
                              <TableCell>
                                {task.payment_record ? (
                                  task.payment_record.run_id ? (
                                    <Chip
                                      label={
                                        task.payment_record.name ||
                                        task.payment_record.run_id
                                      }
                                      size="small"
                                      variant="outlined"
                                      component="a"
                                      href={`/manager/task-payment/history/${task.payment_record.run_id}`}
                                      target="_blank"
                                      clickable
                                      onClick={(e: React.MouseEvent) =>
                                        e.stopPropagation()
                                      }
                                    />
                                  ) : (
                                    <Chip
                                      label={task.payment_record.name || "Payment"}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )
                                ) : (
                                  <Chip
                                    label="No payment record"
                                    size="small"
                                    color="warning"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      {selected.size > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: "sticky",
            bottom: 16,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body1">
            {selected.size} task(s) selected · {formatCurrency(selectedTotal)}
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button onClick={() => setSelected(new Set())} disabled={markLoading}>
              Clear
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handlePreviewMark}
              disabled={markLoading}
            >
              {markLoading && !preview ? (
                <CircularProgress size={20} />
              ) : (
                "Mark Unpaid"
              )}
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={preview !== null}
        onClose={() => !markLoading && closePreview()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Mark these tasks unpaid?</DialogTitle>
        <DialogContent>
          {preview && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This marks {preview.request_count} task(s) totaling{" "}
                {formatCurrency(preview.reverted_amount)} unpaid so they are
                paid in the next payment run. Payment records and batches are
                not changed.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Tasks</TableCell>
                    <TableCell align="right">Marked Unpaid</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.summary.map((row) => (
                    <TableRow key={row.user_key ?? row.email ?? row.name ?? ""}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell align="right">{row.request_count}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.reverted_amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.skipped.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {preview.skipped.length} selected task(s) will be skipped:{" "}
                  {preview.skipped
                    .map((s) => `${s.request_uuid || s.request_key} (${s.reason})`)
                    .join(", ")}
                </Alert>
              )}
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Checkbox
                    checked={markConfirmed}
                    onChange={(e) => setMarkConfirmed(e.target.checked)}
                  />
                }
                label="No money was sent for these tasks; they should be paid in the next run"
              />
              {markError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {markError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} disabled={markLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleExecuteMark}
            disabled={!markConfirmed || markLoading || preview?.request_count === 0}
          >
            {markLoading ? <CircularProgress size={20} /> : "Mark Unpaid"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});
